import type { MetaFunction } from "react-router"

import { ButtonLink } from "~/components/ui/button"
import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import { H2, Lead, P, Quote, Strong } from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// Kjartan's ideas post: five things customers and partners are building (or
// exploring) on Pine59 data, framed as an invitation to build on the APIs.
// Copy ported from Kjartan's Google Doc (2026-09-10).

const post = getPost("built-on-our-data")!

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          One of the best parts of my job is that I have a lot of client
          meetings.
        </Lead>
        <P>
          Some are early conversations with a few slides and an ambitious idea.
          Others involve products already running at serious scale. And
          increasingly, I find myself leaving these meetings thinking:{" "}
          <Strong>
            Wow, this is clever. I know what our data can do, but I love seeing
            people find new ways to apply it.
          </Strong>
        </P>
        <P>
          I'm not going to name names here. Some of these products are live,
          some are being built, and some are simply ideas being explored. A few
          might never happen at all. That's part of building new things.
        </P>
        <P>
          But innovation creates more innovation when ideas are shared. So
          rather than presenting these as customer case studies, I thought I'd
          share some of the ideas I'm seeing and, more importantly, how our data
          could be used to build them.
        </P>

        <BlogCta />

        <H2>Travel guidance that helps when things go wrong</H2>
        <P>
          Imagine a crisis where large numbers of people or vehicles suddenly
          need to get somewhere safely and quickly, to get in or out of
          somewhere.
        </P>
        <P>
          This is where our data could be used to help a system understand how
          roads and populations are actually moving, and combine that
          intelligence with travel guidance to direct traffic and people toward
          the right locations.
        </P>
        <P>
          <Strong>Ways</Strong> can provide intelligence about how roads are
          being used, while <Strong>Areas</Strong> can help understand
          population movement around them.
        </P>
        <P>Travel guidance + location intelligence + emergency response.</P>
        <P>This could save lives. Which is precisely why I like it.</P>

        <H2>
          Connecting what happens outside the store with what happens inside
        </H2>
        <P>
          Retailers have become very good at measuring what happens once
          somebody enters a store: traffic, conversion, dwell time, transactions
          and operational performance.
        </P>
        <P>But the customer journey doesn't begin at the front door.</P>
        <P>
          Our <Strong>Places</Strong> intelligence could be combined with
          in-store data to connect what's happening in the surrounding market
          with what happens once customers walk inside. Suddenly you can start
          connecting market potential, visitation and customer behavior with
          actual store performance.
        </P>
        <P>
          Instead of just knowing <em>what</em> happened, retailers get closer
          to understanding <em>why</em>.
        </P>

        <H2>Building a global operating system for retail</H2>
        <P>Now take that thinking beyond an individual store.</P>
        <P>
          Our data could become one input into a global product helping
          retailers optimize operations across hundreds or thousands of
          locations, combining location intelligence with sales, inventory,
          staffing and other operational data.
        </P>
        <P>
          I particularly like this kind of use case because the customer isn't
          really "using Pine59."
        </P>
        <P>Their product is using Pine59.</P>
        <P>That's exactly what APIs should enable.</P>

        <H2>Putting emergency services where they're actually needed</H2>
        <P>Where should an ambulance be waiting?</P>
        <P>
          The answer changes depending on where people are, how they move, what
          time it is and what's happening around them.
        </P>
        <P>
          This is another area where our data could be useful.{" "}
          <Strong>Areas and Ways</Strong> can provide intelligence about
          population movement and mobility patterns, which could become one
          input into models determining where emergency resources should be
          positioned.
        </P>
        <P>
          Instead of relying only on static assumptions, planners could add
          another signal based on how places and populations actually behave.
        </P>
        <P>That's a pretty good use of data.</P>

        <H2>Making national statistics better</H2>
        <P>
          This one sounds less dramatic than crisis guidance and ambulances.
        </P>
        <P>It might actually have the biggest impact.</P>
        <P>
          Aggregated mobility data can be used as another signal for
          understanding how populations move and change, potentially
          complementing more traditional sources used to produce official
          national statistics.
        </P>
        <P>
          This is particularly interesting because our approach is based on{" "}
          <Strong>Aggregated Location Intelligence</Strong> rather than giving
          customers access to individual device-level location data.
        </P>
        <P>
          National statistical organizations could use this kind of intelligence
          to understand the physical world with greater frequency and detail.
        </P>
        <P>
          Better statistics can lead to better decisions. Better decisions
          eventually affect almost everyone.
        </P>
        <P>Not bad for an API call.</P>

        <H2>And these are just five ideas</H2>
        <P>
          What I find interesting about all of this is that we didn't invent
          these ideas.
        </P>
        <P>Our customers and partners did.</P>
        <P>
          Some will become products. Some won't. Others will evolve into
          something completely different along the way.
        </P>
        <P>That's the fun part.</P>
        <P>
          Pine59 provides Aggregated Location Intelligence about{" "}
          <Strong>Places, Areas and Ways</Strong>. We make it available through
          our products, Console and APIs, but the really interesting part starts
          when someone combines it with something we haven't thought of.
        </P>
        <P>
          Your transaction data. Your sensors. Your models. Your AI. Your
          software. Your industry knowledge. Your slightly crazy idea that
          somebody initially told you wouldn't work.
        </P>
        <Quote>
          That's when data stops being a dataset and becomes a product.
        </Quote>
        <P>So consider this an invitation.</P>
        <P>
          <Strong>
            Connect to our APIs. Take the data. Combine it with something
            interesting. Build something we haven't thought of.
          </Strong>
        </P>
        <P>Then please show it to me.</P>
        <P>Those are my favorite meetings.</P>
        <P>
          <Strong>Let's build!</Strong>
        </P>
        <div className="mt-8">
          <ButtonLink href="/get-started" withArrow>
            Connect to our APIs
          </ButtonLink>
        </div>
      </BlogProse>
    </BlogLayout>
  )
}
